import React from "react";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { Descriptions, Button, Space } from "antd";
import { CopyOutlined, CalendarOutlined } from "@ant-design/icons";
import { PromotionDetailsModalProps } from "./types";
import { useTheme } from "../../context/ThemeContext";
import {
  StyledModal,
  ModalFooter,
  CloseButton,
  SubmitButton,
  TitleIcon,
  TitleText,
  PromoCodeTag,
  DiscountValueText,
  DetailsCard,
  StyledDescriptions,
} from "./Styles";
import { message } from "antd";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const PromotionDetailsModal: React.FC<PromotionDetailsModalProps> = ({
  isOpen,
  onClose,
  promotion,
  onEdit,
  onCopyCode,
  getStatusBadge,
  getTypeLabel,
  isSaving = false,
}) => {
  const { isDarkMode } = useTheme();
  const styledTheme = { mode: isDarkMode ? "dark" : "light" } as DefaultTheme;

  if (!promotion) return null;

  const handleCopyCode = () => {
    const code = promotion.code || promotion.promoCode || "";
    navigator.clipboard.writeText(code);
    message.success(`Code "${code}" copied to clipboard.`);
    onCopyCode(code);
  };

  const handleEdit = () => {
    onClose();
    onEdit(promotion);
  };

  const discountText =
    promotion.type === "percentage"
      ? `${promotion.discountValue}%`
      : promotion.type === "fixed"
      ? `$${promotion.discountValue}`
      : "Buy 1 Get 1 Free";

  return (
    <StyledThemeProvider theme={styledTheme}>
      <StyledModal
      title={
        <Space>
          <TitleIcon />
          Promotion Details
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      width={800}
      zIndex={2000}
      destroyOnClose
      footer={
        <ModalFooter>
          <CloseButton onClick={onClose} disabled={isSaving}>
            Close
          </CloseButton>
          <SubmitButton
            type="primary"
            icon={<FontAwesomeIcon icon={faPen} fontSize={12}/>}
            onClick={handleEdit}
            disabled={isSaving}
          >
            Edit
          </SubmitButton>
        </ModalFooter>
      }
    >
      <DetailsCard>
        <StyledDescriptions column={1} bordered>
          <Descriptions.Item label="Title">
            <TitleText>{promotion.title}</TitleText>
          </Descriptions.Item>

          <Descriptions.Item label="Description">
            {promotion.description || "No description provided"}
          </Descriptions.Item>

          <Descriptions.Item label="Promo Code">
            <Space>
              <PromoCodeTag color="purple">
                {promotion.code || promotion.promoCode || "N/A"}
              </PromoCodeTag>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopyCode}
              >
                Copy
              </Button>
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label="Type">
            {getTypeLabel(promotion.type)}
          </Descriptions.Item>

          <Descriptions.Item label="Discount Value">
            <DiscountValueText>{discountText}</DiscountValueText>
          </Descriptions.Item>

          <Descriptions.Item label="Status">
            {getStatusBadge(promotion)}
          </Descriptions.Item>

          <Descriptions.Item label="Start Date">
            <Space>
              <CalendarOutlined />
              {promotion.startDate
                ? new Date(promotion.startDate).toLocaleDateString()
                : "N/A"}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label="End Date">
            <Space>
              <CalendarOutlined />
              {promotion.endDate
                ? new Date(promotion.endDate).toLocaleDateString()
                : "N/A"}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label="Usage">
            {promotion.usageCount || 0} / {promotion.usageLimit || "∞"}
          </Descriptions.Item>

          <Descriptions.Item label="Minimum Order Amount">
            ${promotion.minOrderAmount || 0}
          </Descriptions.Item>
        </StyledDescriptions>
      </DetailsCard>
    </StyledModal>
    </StyledThemeProvider>
  );
};

export default PromotionDetailsModal;

